import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, TextField, Skeleton, useTheme, InputAdornment } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import { useSubmission } from 'services/Data/SubmissionServices';
import { useUpdateSubmissionDetail, useCreateSubmissionDetail } from 'services/Data/SubmissionDetailServices';
import { createSubmissionDetailPatchDocument } from 'services/Serializers/SubmissionDetailPatchDocument';
import { translateLabel } from 'services/Utilities/labelUtils';
import { useGetExternalDetails } from 'services/Data/AdditionalDetailService';
import { FieldTypeEnumValue } from 'enums/FieldTypeEnum';
import RenderItem from './RenderItem';

const AmountFormatInput = forwardRef(function AmountFormatInput(props, ref) {
  const { onChange, name, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={values => {
        if (!onChange) return;
        onChange({
          target: {
            name,
            value: values.value,
          },
        });
      }}
      thousandSeparator=","
      decimalSeparator="."
      decimalScale={2}
      allowNegative={false}
      valueIsNumericString
      allowLeadingZeros={false}
    />
  );
});

// Text/number/amount input with optional auto-fill children
const InputItem = ({ detail, allDetails = [], level = 0, isReadOnly = false }) => {
  const { submissionId } = useParams();
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const { data: submission, isLoading } = useSubmission(submissionId);
  const updateSubmissionDetailMutation = useUpdateSubmissionDetail(submissionId);
  const createSubmissionDetailMutation = useCreateSubmissionDetail(submissionId);
  const { mutateAsync: getExternalDetails } = useGetExternalDetails();

  const existingSubmissionDetail = submission?.submissionDetails?.find(sd => sd.criteriaTypeDetail_Id === detail.id);
  const inputRef = useRef(null);
  const [validationError, setValidationError] = useState(null);
  const [amountValue, setAmountValue] = useState('');

  const fieldType = detail?.fieldType ?? FieldTypeEnumValue.String;
  const minChars = detail?.minimumNumberOfCharacters ?? null;
  const maxChars = detail?.maximumNumberOfCharacters ?? null;
  const isNumericField = fieldType === FieldTypeEnumValue.Number;
  const isAmountField = fieldType === FieldTypeEnumValue.Amount;
  const supportsLengthValidation =
    fieldType === FieldTypeEnumValue.String ||
    fieldType === FieldTypeEnumValue.Number ||
    fieldType === FieldTypeEnumValue.Amount;

  useEffect(() => {
    if (isAmountField) {
      setAmountValue(existingSubmissionDetail?.value ?? '');
    }
  }, [existingSubmissionDetail?.value, isAmountField]);

  const isAutoFilledField = useMemo(() => {
    return allDetails.some(d => {
      const parents = d.parentCriteriaTypeDetailAdditionalDetails || [];
      return parents.some(parent => parent.associatedCriteriaTypeDetail_Id === detail.id);
    });
  }, [allDetails, detail.id]);

  const isFieldDisabled = isReadOnly || isAutoFilledField;
  const isRequiredField = detail?.isRequired !== false;
  const inputType = isNumericField ? 'number' : 'text';
  const helperTextContent = validationError || translateLabel(detail.description, i18n.language) || undefined;

  const inputPropsConfig = { readOnly: isFieldDisabled };
  if (isNumericField || isAmountField) inputPropsConfig.inputMode = 'decimal';
  if (isNumericField) inputPropsConfig.step = '1';
  if (isAmountField) inputPropsConfig.step = '0.01';
  if (maxChars != null) inputPropsConfig.maxLength = maxChars;
  if (minChars != null) inputPropsConfig.minLength = minChars;

  const inputAdornmentProps = isAmountField
    ? {
        startAdornment: <InputAdornment position="start">$</InputAdornment>,
      }
    : undefined;

  const textFieldInputProps = { ...(inputAdornmentProps ?? {}) };
  if (isAmountField) {
    textFieldInputProps.inputComponent = AmountFormatInput;
  }

  const getExternalData = async (typedValue, targetDetail) => {
    const parents = targetDetail.parentCriteriaTypeDetailAdditionalDetails || [];
    if (parents.length === 0) return;

    const externalValue = await getExternalDetails({
      backendUrl: parents[0]?.backendUrl,
      params: {
        additionalDetailId: parents[0]?.additionalDetail_Id,
        typedValue,
      },
    });

    const parsedExternal = typeof externalValue === 'string' ? JSON.parse(externalValue) : externalValue || {};

    for (const element of parents) {
      try {
        const fieldName = element.field;
        if (!fieldName) continue;

        const fieldValue = parsedExternal?.[fieldName];
        if (fieldValue === null || fieldValue === undefined || fieldValue === '') continue;

        const valueToSave = String(fieldValue);
        const existingForParent = (submission?.submissionDetails || []).find(
          sd => sd.criteriaTypeDetail_Id === element.associatedCriteriaTypeDetail_Id,
        );

        if (existingForParent) {
          const patchDoc = createSubmissionDetailPatchDocument({ value: valueToSave });
          await updateSubmissionDetailMutation.mutateAsync({
            submissionDetailId: existingForParent.id,
            patchDocument: patchDoc,
          });
        } else {
          const newSubmissionDetail = {
            id: crypto.randomUUID(),
            submission_Id: submissionId,
            criteriaTypeDetail_Id: element.associatedCriteriaTypeDetail_Id,
            associatedCriteriaTypeDetail_Id: element.associatedCriteriaTypeDetail_Id,
            description: '',
            value: valueToSave,
          };
          await createSubmissionDetailMutation.mutateAsync(newSubmissionDetail);
        }
      } catch (err) {
        console.error('Failed to resolve external detail', err);
      }
    }
  };

  const handleAmountChange = event => {
    if (isFieldDisabled) return;
    setAmountValue(event.target.value ?? '');
    if (validationError) setValidationError(null);
  };

  const handleBlur = async event => {
    if (isReadOnly) return;
    const eventValue = event?.target?.value ?? '';
    const newValue = isAmountField ? amountValue ?? '' : eventValue;
    const currentValue = existingSubmissionDetail?.value || '';

    if (newValue === currentValue) {
      setValidationError(null);
      return;
    }

    const trimmedValue = (newValue || '').trim();
    const shouldValidateLength = supportsLengthValidation;

    if (shouldValidateLength && minChars != null && trimmedValue.length < minChars) {
      setValidationError(t('submission.validation.minCharacters', { count: minChars }));
      return;
    }

    if (shouldValidateLength && maxChars != null && trimmedValue.length > maxChars) {
      setValidationError(t('submission.validation.maxCharacters', { count: maxChars }));
      return;
    }

    setValidationError(null);

    try {
      if (existingSubmissionDetail) {
        const patchDoc = createSubmissionDetailPatchDocument({ value: newValue });
        await updateSubmissionDetailMutation.mutateAsync({
          submissionDetailId: existingSubmissionDetail.id,
          patchDocument: patchDoc,
        });
      } else if (newValue !== '') {
        const newSubmissionDetail = {
          id: crypto.randomUUID(),
          submission_Id: submissionId,
          criteriaTypeDetail_Id: detail.id,
          description: '',
          value: newValue,
        };
        await createSubmissionDetailMutation.mutateAsync(newSubmissionDetail);
      }
      await getExternalData(newValue, detail);
    } catch (error) {
      console.error('Failed to update input value:', error);
      if (isAmountField) {
        setAmountValue(currentValue ?? '');
      } else if (inputRef.current) {
        inputRef.current.value = currentValue;
      }
    }
  };

  const textFieldValueProps = isAmountField
    ? {
        value: amountValue ?? '',
        onChange: handleAmountChange,
        name: `amount-input-${detail.id}`,
      }
    : {
        defaultValue: existingSubmissionDetail?.value || '',
      };

  const indentAmount = level * 4;

  if (isLoading) {
    return (
      <Box sx={{ pl: indentAmount, py: 1 }}>
        <Skeleton variant="rounded" height={56} animation="wave" />
      </Box>
    );
  }

  const childDetails = allDetails
    .filter(child => child.parentCriteriaTypeDetail_Id === detail.id)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return (
    <Box>
      <Box sx={{ pl: indentAmount, py: 1 }}>
        <TextField
          inputRef={inputRef}
          key={existingSubmissionDetail?.value}
          label={translateLabel(detail.name, i18n.language)}
          {...textFieldValueProps}
          type={inputType}
          slotProps={{
            inputLabel: {
              shrink: existingSubmissionDetail?.value ? true : undefined,
              required: isRequiredField,
            },
          }}
          onBlur={handleBlur}
          helperText={helperTextContent}
          error={Boolean(validationError)}
          fullWidth
          disabled={isFieldDisabled || updateSubmissionDetailMutation.isPending || createSubmissionDetailMutation.isPending}
          inputProps={inputPropsConfig}
          InputProps={textFieldInputProps}
          required={isRequiredField}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
              transition: 'background-color 0.2s ease',
              '&:hover': {
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 210, 0, 0.05)' : 'rgba(212, 175, 55, 0.05)',
              },
              '&.Mui-focused': {
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 210, 0, 0.08)' : 'rgba(212, 175, 55, 0.08)',
              },
            },
            '& .MuiOutlinedInput-root.Mui-disabled': {
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
            },
            '& .MuiInputLabel-root': {
              fontWeight: level === 0 ? 600 : 400,
              color: theme.palette.text.primary,
            },
            '& .MuiFormHelperText-root': {
              color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
              fontStyle: 'italic',
            },
          }}
        />
      </Box>

      {childDetails.length > 0 && (
        <Box>
          {childDetails.map(childDetail => (
            <RenderItem key={childDetail.id} detail={childDetail} allDetails={allDetails} isReadOnly={isReadOnly} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default InputItem;
